import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useTimeIntervalContext} from '@shared/providers/TimeIntervalProvider'
import type {TimeIntervalContextProps} from '@shared/providers/TimeIntervalProvider'
import {
  endOfShift,
  getPreviousShiftRange,
  isSoFarRangeId,
  PREVIOUS_SHIFT_RANGE_ID,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfShift,
  startOfWeek,
  startOfYear,
} from '@shared/utils/timeRange'

type TimeNavigationDirection = 'back' | 'forward'

interface UseTimeNavigationProps {
  hasError?: boolean
  isLoading?: boolean
  hasData?: boolean
}

interface UseTimeNavigationReturn {
  handleTimeNavigation: (direction: TimeNavigationDirection) => void
  isForwardDisabled: boolean
  isBackwardDisabled: boolean
}

interface TimeInterval {
  startTime: Date
  endTime: Date
}

/**
 * Hook for time navigation in charts and timelines
 * Provides functionality to navigate backward and forward in time
 * while respecting the current time constraints
 *
 * @param props - Configuration options
 * @param props.hasError - Whether there is an error in the data
 * @param props.isLoading - Whether data is loading
 * @param props.hasData - Whether data is available
 */
export const useTimeNavigation = (props?: UseTimeNavigationProps): UseTimeNavigationReturn => {
  const {hasError = false, isLoading = false, hasData = true} = props || {}

  const {startTime, endTime, timeRange, updateStartTime, updateEndTime} =
    useTimeIntervalContext() as TimeIntervalContextProps

  // Keep track of the last valid time interval
  const lastValidIntervalRef = useRef<TimeInterval>({
    startTime: new Date(startTime),
    endTime: new Date(endTime),
  })

  useEffect(() => {
    if (!isLoading && !hasError && hasData) {
      lastValidIntervalRef.current = {
        startTime: new Date(startTime),
        endTime: new Date(endTime),
      }
    }
  }, [startTime, endTime, isLoading, hasError, hasData])

  const addHours = (date: Date, hours: number): Date => {
    const next = new Date(date)
    next.setTime(next.getTime() + hours * 60 * 60 * 1000)
    return next
  }

  const addDays = (date: Date, days: number): Date => {
    const next = new Date(date)
    next.setDate(next.getDate() + days)
    return next
  }

  const addMonths = (date: Date, months: number): Date => {
    const next = new Date(date)
    next.setMonth(next.getMonth() + months)
    return next
  }

  const isSoFarRange = isSoFarRangeId(timeRange)

  const addSoFarPeriod = useCallback(
    (date: Date, direction: TimeNavigationDirection): Date => {
      const multiplier = direction === 'back' ? -1 : 1
      switch (timeRange) {
        case 'day-so-far':
          return addDays(date, multiplier)
        case 'week-so-far':
          return addDays(date, multiplier * 7)
        case 'month-so-far':
          return addMonths(date, multiplier)
        case 'quarter-so-far':
          return addMonths(date, multiplier * 3)
        case 'year-so-far':
          return addMonths(date, multiplier * 12)
        default:
          return date
      }
    },
    [timeRange],
  )

  const getSoFarStart = useCallback(
    (end: Date): Date => {
      switch (timeRange) {
        case 'day-so-far':
          return startOfDay(end)
        case 'week-so-far':
          return startOfWeek(end)
        case 'month-so-far':
          return startOfMonth(end)
        case 'quarter-so-far':
          return startOfQuarter(end)
        case 'year-so-far':
          return startOfYear(end)
        case 'shift-so-far':
          return startOfShift(end)
        default:
          return startOfDay(end)
      }
    },
    [timeRange],
  )

  const getPreviousSoFarStart = useCallback(
    (end: Date): Date => {
      const previousEnd = addSoFarPeriod(end, 'back')
      return getSoFarStart(previousEnd)
    },
    [addSoFarPeriod, getSoFarStart],
  )

  const isForwardDisabled = useMemo(() => false, [])

  const isBackwardDisabled = useMemo(() => false, [])

  const handleTimeNavigation = useCallback(
    (direction: TimeNavigationDirection) => {
      // "Previous shift" (AB#6936) pages whole shifts and never past the last
      // completed one: the running shift is "shift so far".
      if (timeRange === PREVIOUS_SHIFT_RANGE_ID) {
        if (direction === 'back') {
          updateStartTime(startOfShift(new Date(startTime.getTime() - 1)))
          updateEndTime(new Date(startTime))
          return
        }
        const nextEnd = endOfShift(new Date(endTime))
        if (nextEnd.getTime() > startOfShift(new Date()).getTime()) {
          const {start, end} = getPreviousShiftRange(new Date())
          updateStartTime(start)
          updateEndTime(end)
          return
        }
        updateStartTime(new Date(endTime))
        updateEndTime(nextEnd)
        return
      }
      if (isSoFarRange) {
        if (timeRange === 'shift-so-far') {
          const now = new Date()
          if (direction === 'back') {
            const newEndTime = new Date(startTime)
            const newStartTime = addHours(newEndTime, -8)
            updateStartTime(newStartTime)
            updateEndTime(newEndTime)
            return
          }

          const candidateStart = new Date(endTime)
          const candidateEnd = addHours(candidateStart, 8)
          if (candidateEnd.getTime() >= now.getTime()) {
            const newEndTime = now
            const newStartTime = startOfShift(now)
            updateStartTime(newStartTime)
            updateEndTime(newEndTime)
            return
          }
          const newEndTime = candidateEnd
          const newStartTime = candidateStart
          updateStartTime(newStartTime)
          updateEndTime(newEndTime)
          return
        }

        if (direction === 'back') {
          const newEndTime = new Date(startTime)
          const previousPeriodAnchor = addSoFarPeriod(newEndTime, 'back')
          const newStartTime = getSoFarStart(previousPeriodAnchor)
          updateStartTime(newStartTime)
          updateEndTime(newEndTime)
          return
        }

        const candidateEnd = addSoFarPeriod(endTime, 'forward')
        if (candidateEnd.getTime() >= new Date().getTime()) {
          const now = new Date()
          const newEndTime = now
          let newStartTime = getSoFarStart(now)
          if (newStartTime.getTime() === newEndTime.getTime()) {
            newStartTime = getPreviousSoFarStart(newEndTime)
          }
          updateStartTime(newStartTime)
          updateEndTime(newEndTime)
          return
        }
        const newEndTime = candidateEnd
        let newStartTime = getSoFarStart(candidateEnd)
        if (newStartTime.getTime() === newEndTime.getTime()) {
          newStartTime = getPreviousSoFarStart(newEndTime)
        }
        updateStartTime(newStartTime)
        updateEndTime(newEndTime)
        return
      }

      const timeDiff = endTime.getTime() - startTime.getTime()
      const newStartTime = new Date(startTime)
      const newEndTime = new Date(endTime)

      if (direction === 'back') {
        newStartTime.setTime(startTime.getTime() - timeDiff)
        newEndTime.setTime(endTime.getTime() - timeDiff)
      } else {
        newStartTime.setTime(startTime.getTime() + timeDiff)
        newEndTime.setTime(endTime.getTime() + timeDiff)

        if (newEndTime.getTime() > new Date().getTime()) {
          const now = new Date().getTime()
          const overshoot = newEndTime.getTime() - now
          newEndTime.setTime(now)
          newStartTime.setTime(newStartTime.getTime() - overshoot)
        }
      }

      updateStartTime(newStartTime)
      updateEndTime(newEndTime)
    },
    [
      startTime,
      endTime,
      updateStartTime,
      updateEndTime,
      isBackwardDisabled,
      isSoFarRange,
      timeRange,
      addSoFarPeriod,
      getSoFarStart,
      getPreviousSoFarStart,
    ],
  )

  return {
    handleTimeNavigation,
    isForwardDisabled,
    isBackwardDisabled,
  }
}
